import { v4 as uuid } from "uuid";
import { sendMessage } from "state/websocket/operations";
import type { Dispatch } from "redux";
import type { SelectedQuestionAndFocus } from "screens/thread/ConversationContext";
import { useAnswerFocusOptions } from "hooks/useAnswerFocusOptions";
import { temporaryProjectManager } from "screens/landing/tabs/dashboard/ProjectWatchList/temporaryProjectManager";

interface Entity {
  entity: string;
  value: string;
}

type RequestEntities = Entity[];

interface SubmitDueDiligenceProps {
  companyTicker: string;
  companyStockExchange: string;
  isTicker: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dispatch: Dispatch<any>;
  onConversationOpen?: (conversationId: string) => void;
  conversationId?: string;
  showToastOnly?: boolean;
  onSubmitted?: () => void;
  additionalEntities?: RequestEntities;
  portfolioId?: string;
  reRunProjectId?: string;
  companyStockType?: string | null | undefined;
  modelUpgradedQuestionAndFocus?: SelectedQuestionAndFocus;
  scheduledStartTimestamp?: number;
  setShowConversationMessages?: (value: boolean) => void;
  companyName?: string;
  companyDescription?: string;
  companyWebsite?: string;
  companyLogoUrl?: string;
}

export const useDueDiligenceFocusOptions = () => {
  const { answerFocusOptions } = useAnswerFocusOptions("due_diligence");
  const defaultFirstFocus = answerFocusOptions?.[0]?.key ?? "";

  return {
    defaultFirstFocus,
    answerFocusOptions,
  };
};

const useSubmitDueDiligence = ({
  companyTicker,
  companyStockExchange,
  isTicker,
  dispatch,
  onConversationOpen,
  conversationId,
  onSubmitted,
  additionalEntities = [],
  portfolioId,
  reRunProjectId,
  modelUpgradedQuestionAndFocus,
  setShowConversationMessages,
  companyStockType,
  companyName,
  companyDescription,
  companyWebsite,
  companyLogoUrl,
}: SubmitDueDiligenceProps) => {
  // No longer calling useConversationContext hook internally
  const entities: RequestEntities = [];
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  if (isTicker) {
    entities.push({ entity: "company_ticker", value: companyTicker.trim() });
  } else {
    entities.push({ entity: "company_name", value: companyTicker.trim() });
  }
  if (companyStockExchange && companyStockExchange.length > 0) {
    entities.push({ entity: "company_stock_exchange", value: companyStockExchange });
  }
  if (portfolioId) {
    entities.push({ entity: "portfolio_id", value: portfolioId });
  }
  if (timezone) {
    entities.push({ entity: "client_time_zone", value: timezone });
  }
  if (reRunProjectId) {
    entities.push({ entity: "as_rerun_of_project_id", value: reRunProjectId });
  }
  if (modelUpgradedQuestionAndFocus && modelUpgradedQuestionAndFocus.question && modelUpgradedQuestionAndFocus.focus) {
    entities.push({ entity: "rerun_caused_by_model_upgrade_question", value: modelUpgradedQuestionAndFocus.question });
    entities.push({ entity: "rerun_caused_by_model_upgrade_answer_focus", value: modelUpgradedQuestionAndFocus.focus });
  }

  const newConversationId = conversationId ?? uuid();

  dispatch(
    sendMessage({
      conversationId: newConversationId,
      intent: `/create_due_diligence_project`,
      entities: [...entities, ...additionalEntities],
    })
  );

  // Add a temporary project tile to show immediately in the UI
  if (isTicker) {
    temporaryProjectManager.addTemporaryProject({
      ticker: companyTicker.trim(),
      exchange: companyStockExchange,
      securityType: companyStockType || undefined,
      companyLogoUrl: companyLogoUrl,
      companyName: companyName || undefined,
      companyDescription: companyDescription || undefined,
      companyWebsite: companyWebsite || undefined,
      // createdAt is added automatically by the manager
    });
  }

  if (onSubmitted) {
    onSubmitted();
  } else {
    onConversationOpen && onConversationOpen(newConversationId);
    onConversationOpen && setShowConversationMessages && setShowConversationMessages(true);
  }
};

export default useSubmitDueDiligence;
